import { Typography } from '@mui/material';
import { marked, Tokens } from 'marked';
import ReactDOMServer from 'react-dom/server';

const renderer = new marked.Renderer();

renderer.link = function ({ href, title, tokens }: Tokens.Link) {
  const isExternal = href.startsWith('http');
  const linkAttributes = isExternal ? 'target="_blank" rel="noopener noreferrer"' : '';

  const linkText = tokens.map((token) => token.raw).join('');
  return `<a href="${href}" title="${title || ''}" ${linkAttributes}>${linkText}</a>`;
};

renderer.heading = function ({ tokens, depth }) {
  if (!this.parser) {
    throw new Error('Parser is undefined');
  }
  const parsedText = this.parser.parseInline(tokens);

  const headingFontWeightMap = {
    h1: 900,
    h2: 800,
    h3: 700,
    h4: 600,
    h5: 500,
    h6: 500
  };

  const headingFontSizeMap = {
    h1: '42px',
    h2: '38px',
    h3: '36px',
    h4: '28px',
    h5: '24px',
    h6: '22px'
  };

  const headingLevel = `h${depth}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const Tag = (
    <Typography
      component={headingLevel}
      style={{
        marginTop: '28px',
        lineHeight: '1.3',
        fontFamily: 'Montserrat',
        fontSize: headingFontSizeMap[headingLevel],
        fontWeight: headingFontWeightMap[headingLevel]
      }}
      dangerouslySetInnerHTML={{ __html: parsedText }}
    />
  );

  return ReactDOMServer.renderToString(Tag);
};

renderer.paragraph = function ({ tokens }) {
  if (!this.parser) {
    throw new Error('Parser is undefined');
  }
  const parsedText = this.parser.parseInline(tokens);

  const Paragraph = (
    <Typography
      variant="body1"
      style={{
        margin: 0,
        lineHeight: '1.6',
        fontFamily: 'Montserrat',
        fontSize: '21px'
      }}
      dangerouslySetInnerHTML={{ __html: parsedText }}
    />
  );

  return ReactDOMServer.renderToString(Paragraph);
};

renderer.listitem = function (token) {
  const Li = (
    <li
      style={{
        listStyle: 'none',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <div style={{ display: 'flex', minWidth: '24px' }}>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5 12L10 17L20 7"
            stroke="#F08200"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <span
        style={{
          marginLeft: '8px',
          fontFamily: 'Montserrat',
          fontWeight: 400,
          fontSize: '21px'
        }}
      >
        {token.text}
      </span>
    </li>
  );

  return ReactDOMServer.renderToString(Li);
};

renderer.list = function (token) {
  const listType = token.ordered ? 'ol' : 'ul';
  const items = token.items.map((item) => this.listitem(item)).join('');

  return `
    <${listType} style="padding-left: 0; margin: 16px 0;">
      ${items}
    </${listType}>
  `;
};

function useMarkdown() {
  return (markdownText: string) => {
    marked.use({ renderer });
    return marked.parse(markdownText);
  };
}

export default useMarkdown;
